import { addDays as _addDays, addMinutes, format as formatFunction, isValid, parse as _parse, parseISO as _parseISO } from 'date-fns';
import { utcToZonedTime, getTimezoneOffset, toDate } from 'date-fns-tz';
import _isEmpty from 'lodash/isEmpty';
import _isNumber from 'lodash/isNumber';
var DateUtils = {
  format: function format(date, _format) {
    if (_format) {
      return formatFunction(date, _format);
    }
    return date;
  },
  parse: function parse(dateString, format) {
    var _ref = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
      _ref$referenceDate = _ref.referenceDate,
      referenceDate = _ref$referenceDate === void 0 ? new Date(Date.now()) : _ref$referenceDate,
      _ref$timezone = _ref.timezone,
      timezone = _ref$timezone === void 0 ? 'UTC' : _ref$timezone;
    var timezonedReferenceDate = utcToZonedTime(referenceDate, timezone);
    return _parse(dateString, format, timezonedReferenceDate);
  },
  /**
   *
   * @param {string} dateString: ISO datetime string in UTC (i.e: "2022-01-06T23:00:00.000Z")
   * @param {object} params: if timezone is defined, it is used to return an aware datetime
   * @returns
   */
  parseISO: function parseISO(dateString) {
    var timezone = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : undefined;
    var date = _parseISO(dateString);
    if (!timezone || !isValid(date)) {
      return date;
    }
    return toDate(date.getTime() - getTimezoneOffset(timezone, date), {
      timeZone: timezone
    });
  },
  getCurrentDate: function getCurrentDate() {
    return new Date();
  },
  getTimezoneAwareNow: function getTimezoneAwareNow(timezone) {
    var _ref2 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      format = _ref2.format;
    var currentDate = this.getCurrentDate();
    if (!timezone) {
      return this.format(currentDate, format);
    }
    var currentTimezoneDate = utcToZonedTime(currentDate, timezone);
    return this.format(currentTimezoneDate, format);
  },
  addDays: function addDays(days) {
    var _ref3 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref3$date = _ref3.date,
      date = _ref3$date === void 0 ? new Date() : _ref3$date,
      format = _ref3.format;
    return this.format(_addDays(date, days), format);
  },
  parseHourInt: function parseHourInt(hourInt) {
    var _ref4 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref4$referenceDate = _ref4.referenceDate,
      referenceDate = _ref4$referenceDate === void 0 ? new Date() : _ref4$referenceDate;
    if (!hourInt && typeof hourInt !== 'number') {
      return Number.NaN;
    }
    var normalizedHourInt = hourInt === 2400 ? 0 : hourInt;
    var timeString = normalizedHourInt.toString().padStart(4, '0');
    return _parse(timeString, 'HHmm', referenceDate);
  },
  getIsoDayOfWeek: function getIsoDayOfWeek(date) {
    var _ref5 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      format = _ref5.format;
    // monday = 1, sunday = 7
    var dateObject = format ? _parse(date, format, new Date()) : date;
    return +this.format(dateObject, 'i');
  },
  /**
   *
   * @param {string} startTime ISO datetime string
   * @param {string} endTime ISO datetime string
   * @param {string} timezone if defined, it must be a IANA valid timezone and it
   *                          will be used to parse start and end time
   * @param {function} getNow It is expected to return a Date object representing
   *                   current time. It defaults to new Date().
   * @returns true if now is between the given dates
   */
  isNowBetweenDates: function isNowBetweenDates(startTime, endTime) {
    var timezone = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
    var getNow = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : function () {
      return new Date();
    };
    var now = getNow();
    var startDate = !_isEmpty(startTime) && this.parseISO(startTime, timezone);
    var endDate = !_isEmpty(endTime) && this.parseISO(endTime, timezone);
    return (_isEmpty(startTime) || startDate < now) && (_isEmpty(endTime) || now < endDate);
  },
  intervalsBetweenHoursInt: function intervalsBetweenHoursInt(startHourInt, finalHourInt) {
    var timezone = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'UTC';
    var _ref6 = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {},
      _ref6$rate = _ref6.rate,
      rate = _ref6$rate === void 0 ? 30 : _ref6$rate,
      format = _ref6.format,
      _ref6$onlyFuture = _ref6.onlyFuture,
      onlyFuture = _ref6$onlyFuture === void 0 ? false : _ref6$onlyFuture,
      _ref6$referenceDate = _ref6.referenceDate,
      referenceDate = _ref6$referenceDate === void 0 ? new Date() : _ref6$referenceDate;
    var intervals = [];
    if (!_isNumber(startHourInt) || !_isNumber(finalHourInt) || !timezone) return intervals;
    var nowDate = this.getTimezoneAwareNow(timezone);
    var currentDate = this.parseHourInt(startHourInt, {
      referenceDate: referenceDate
    });
    var startDate = this.parseHourInt(startHourInt, {
      referenceDate: referenceDate
    });
    var finalDate = this.parseHourInt(finalHourInt, {
      referenceDate: referenceDate
    });
    if (finalDate <= startDate) {
      finalDate = _addDays(finalDate, 1);
    }
    currentDate.setMinutes(0);
    while (currentDate < finalDate) {
      if (currentDate >= startDate && (currentDate > nowDate || !onlyFuture)) {
        var formattedDate = this.format(currentDate, format);
        intervals.push(formattedDate);
      }
      currentDate = addMinutes(currentDate, rate);
    }
    return intervals;
  }
};
export default DateUtils;